import { css } from '@emotion/react';
import { ButtonGroup, CircularProgress, IconButton, Sheet, Snackbar, styled, Typography } from '@mui/joy';
import { useMap } from '@vis.gl/react-maplibre';
import { Fragment, lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Transition } from 'react-transition-group';

import InfoIcon from '@/assets/icons/info.svg?react';
import LayersIcon from '@/assets/icons/layers.svg?react';
import LocationIcon from '@/assets/icons/location-small.svg?react';
import SearchIcon from '@/assets/icons/search.svg?react';
import { useIsBackend } from '@/hooks/useIsBackend';
import { I18N_DE } from '@/i18n/de';
import { MAIN_BREAKPOINT } from '@/modules/MAIN_BREAKPOINT';
import { MAP_FLY_TO_ZOOM } from '@/modules/MAP_FLY_TO_ZOOM';
import { getCurrentPosition } from '@/modules/getCurrentPosition';

const InfoModal = lazy(async () => ({
  default: (await import('@/components/modals/InfoModal')).InfoModal,
}));
const LayersModal = lazy(async () => ({
  default: (await import('@/components/modals/LayersModal')).LayersModal,
}));
const MapGeocoderAutocomplete = lazy(async () => ({
  default: (await import('@/components/map/MapGeocoderAutocomplete')).MapGeocoderAutocomplete,
}));

const Root = styled(Sheet)(
  ({ theme }) => css`
    position: absolute;
    top: ${theme.spacing(2)};
    left: 50%;
    border-radius: ${theme.radius.lg};
    z-index: 1;
    visibility: hidden;
    transform: translateX(-50%) translateY(calc(-100% - ${theme.spacing(2)}));
    opacity: 0;
    transition:
      transform 300ms,
      opacity 300ms;

    ${theme.breakpoints.up(MAIN_BREAKPOINT)} {
      top: ${theme.spacing(4)};
    }
  `,
);

const StyledButtonGroup = styled(ButtonGroup)(
  ({ theme }) => css`
    --IconButton-size: 3.5rem;
    --ButtonGroup-separatorSize: 0;
    --ButtonGroup-radius: ${theme.radius.lg};

    .MuiIconButton-root:hover {
      background-color: rgba(224, 224, 224, 0.75);
    }
  `,
);

export function MapMenu() {
  const [geocoderOpen, setGeocoderOpen] = useState(false);

  const [geolocationLoading, setGeolocationLoading] = useState(false);
  const [geolocationError, setGeolocationError] = useState(false);

  const [layersOpen, setLayersOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  const map = useMap();

  const rootNodeRef = useRef<HTMLDivElement>(null);

  const geocoderAutocompleteNodeRef = useRef<HTMLDivElement>(null);
  const geocoderAutocompleteInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (geocoderOpen) {
      setTimeout(() => geocoderAutocompleteInputRef.current?.focus(), 300);
    }
  }, [geocoderOpen]);

  const { isBackend } = useIsBackend();

  return (
    <Fragment>
      <Transition in={!geocoderOpen} nodeRef={rootNodeRef} timeout={300}>
        {(state) => (
          <Root
            ref={rootNodeRef}
            sx={{
              ...(state !== 'exited' && { visibility: 'visible' }),
              ...(['entering', 'entered'].includes(state) && {
                transform: 'translateX(-50%)',
                opacity: 1,
              }),
            }}
          >
            <StyledButtonGroup color="neutral" size="lg" variant="plain">
              <IconButton onClick={() => setGeocoderOpen(true)} title="Adresse, Ort suchen">
                <SearchIcon />
              </IconButton>
              <IconButton
                disabled={geolocationLoading}
                title="Aktuelle GPS Position"
                onClick={async () => {
                  try {
                    setGeolocationLoading(true);
                    const position = await getCurrentPosition({ timeout: 5000 });
                    setGeolocationLoading(false);

                    map.current?.flyTo({
                      center: [position.coords.longitude, position.coords.latitude],
                      ...(map.current?.getZoom() < MAP_FLY_TO_ZOOM && { zoom: MAP_FLY_TO_ZOOM }),
                    });
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  } catch (error) {
                    setGeolocationLoading(false);
                    setGeolocationError(true);
                  }
                }}
              >
                {geolocationLoading ? <CircularProgress /> : <LocationIcon />}
              </IconButton>
              <IconButton onClick={() => setLayersOpen(true)} title="Kartenelemente">
                <LayersIcon />
              </IconButton>
              {!isBackend && (
                <IconButton onClick={() => setInfoOpen(true)} title="Informationen">
                  <InfoIcon />
                </IconButton>
              )}
            </StyledButtonGroup>
          </Root>
        )}
      </Transition>

      <Transition in={geocoderOpen} nodeRef={geocoderAutocompleteNodeRef} timeout={300}>
        {(state) => (
          <MapGeocoderAutocomplete
            ref={geocoderAutocompleteNodeRef}
            inputRef={geocoderAutocompleteInputRef}
            onClose={() => setGeocoderOpen(false)}
            sx={{
              ...(state !== 'exited' && { visibility: 'visible' }),
              ...(['entering', 'entered'].includes(state) && {
                transform: 'translateX(-50%)',
                opacity: 1,
              }),
            }}
          />
        )}
      </Transition>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={5000}
        onClose={() => setGeolocationError(false)}
        open={geolocationError}
        sx={{ '--Snackbar-inset': '96px' }}
      >
        <Typography color="danger" fontWeight="bold" textAlign="center" whiteSpace="nowrap">
          {I18N_DE.geolocationError}
        </Typography>
      </Snackbar>

      {layersOpen ? (
        <Suspense>
          <LayersModal onClose={() => setLayersOpen(false)} />
        </Suspense>
      ) : null}
      {infoOpen ? (
        <Suspense>
          <InfoModal onClose={() => setInfoOpen(false)} />
        </Suspense>
      ) : null}
    </Fragment>
  );
}
